// PropertyLocationModal — expanded location detail overlay opened from
// the property carousel cards inside the booking widget.
//
// Triggered by the small location pill on each card (map-pin icon). The
// caller is expected to have ALSO added the property to the enquiry so
// the accommodation stays visually selected while the modal is open and
// after it closes.
//
// Content:
//   • Enlarged 16:9 satellite map (same coordinates + provider as the
//     card mini-map so the two surfaces feel like one continuous view).
//   • Property name headline.
//   • "How to get there" — car, public transport, airport (i18n).
//   • Coordinates in decimal-degree form.
//   • Global contact email (xaluca@xaluca.com).
//
// Behaviour:
//   • Portalled to <body> so it lives above the sticky summary + audio
//     coordinator without any parent transform interference.
//   • Backdrop + Escape close.
//   • Full-screen on mobile, centred panel on desktop.
//   • The embedded map is intentionally non-interactive (drag/zoom off)
//     to match the mini-map's "fixed inside its container" behaviour.

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Car, Bus, Plane, Mail, Compass } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getHotelLocation } from "@/lib/hotelLocations";
import PropertyCardMiniMap from "@/components/PropertyCardMiniMap";

const pick = (dict, lang) =>
  typeof dict === "string" ? dict : dict?.[lang] || dict?.en || dict?.fr || "";

const COPY = {
  overline: {
    en: "Property location",
    fr: "Emplacement de l'hébergement",
    es: "Ubicación del alojamiento",
  },
  howToGetThere: {
    en: "How to get there",
    fr: "Comment s'y rendre",
    es: "Cómo llegar",
  },
  coordinates: {
    en: "Coordinates",
    fr: "Coordonnées",
    es: "Coordenadas",
  },
  contactEmail: {
    en: "Contact email",
    fr: "E-mail de contact",
    es: "Email de contacto",
  },
  close: { en: "Close", fr: "Fermer", es: "Cerrar" },
  car: { en: "By car", fr: "En voiture", es: "En coche" },
  transport: { en: "Public transport", fr: "Transports en commun", es: "Transporte público" },
  airport: { en: "Nearest airport", fr: "Aéroport le plus proche", es: "Aeropuerto más cercano" },
  address: { en: "Address", fr: "Adresse", es: "Dirección" },
};

const CONTACT_EMAIL = "xaluca@xaluca.com";

const formatCoord = (c) => (typeof c === "number" ? c.toFixed(6) : String(c));

const PropertyLocationModal = ({ open, onClose, hotel }) => {
  const { lang } = useLanguage();

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    // Lock body scroll so the map underneath doesn't scroll through.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open || !hotel || typeof document === "undefined") return null;

  const loc = getHotelLocation(hotel.slug, { location: hotel.location });
  const coords = loc?.coords || null;
  const address = pick(loc?.address, lang);
  const gt = loc?.getting_there || {};

  const rows = [
    { icon: Car, label: pick(COPY.car, lang), value: pick(gt.car, lang) },
    { icon: Bus, label: pick(COPY.transport, lang), value: pick(gt.transport, lang) },
    { icon: Plane, label: pick(COPY.airport, lang), value: pick(gt.airport, lang) },
  ].filter((r) => Boolean(r.value));

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6 md:py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`property-location-modal-title-${hotel.slug}`}
      data-testid="property-location-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={pick(COPY.close, lang)}
        onClick={onClose}
        className="absolute inset-0 w-full h-full bg-[#14100E]/85 backdrop-blur-sm"
        data-testid="property-location-modal-backdrop"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-3xl max-h-full overflow-y-auto bg-[#FDFBF7] text-[#2C2621] border border-[#D4A373]/40 shadow-[0_28px_60px_-16px_rgba(0,0,0,0.65)]"
        data-testid="property-location-modal-panel"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={pick(COPY.close, lang)}
          className="absolute top-3 right-3 md:top-4 md:right-4 z-10 inline-flex items-center justify-center w-9 h-9 bg-[#FDFBF7]/95 text-[#2C2621] border border-[#2C2621]/15 hover:bg-[#2C2621] hover:text-[#FDFBF7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
          data-testid="property-location-modal-close"
        >
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>

        {/* Enlarged satellite map — 16:9. Fixed inside the panel via the
            shared PropertyCardMiniMap component (drag/zoom off) so the
            tile sits exactly where it renders — no parallax, no
            floating, no unexpected re-centres. */}
        <PropertyCardMiniMap
          hotelSlug={hotel.slug}
          hotelName={hotel.name}
          hotelLocation={hotel.location}
          hotelCategory={hotel.category}
          aspectRatio="16/9"
          className="border-0 rounded-none"
        />

        <div className="px-5 md:px-8 pt-5 md:pt-6 pb-6 md:pb-8">
          <p
            className="text-[10px] tracking-[0.28em] uppercase text-[#A07042]"
            data-testid="property-location-modal-overline"
          >
            {pick(COPY.overline, lang)}
          </p>
          <h3
            id={`property-location-modal-title-${hotel.slug}`}
            className="mt-1.5 font-serif-x text-2xl md:text-3xl tracking-tight leading-[1.1] text-[#2C2621]"
            data-testid="property-location-modal-title"
          >
            {hotel.name}
          </h3>
          {address ? (
            <p
              className="mt-1.5 text-sm text-[#5C5248] leading-snug"
              data-testid="property-location-modal-address"
            >
              {address}
            </p>
          ) : null}

          {/* How to get there */}
          {rows.length > 0 ? (
            <section
              className="mt-6 border-t border-[#2C2621]/10 pt-5"
              data-testid="property-location-modal-directions"
            >
              <h4 className="text-[10px] tracking-[0.28em] uppercase text-[#A07042] mb-3 inline-flex items-center gap-2">
                <Compass className="w-3.5 h-3.5" strokeWidth={1.8} />
                {pick(COPY.howToGetThere, lang)}
              </h4>
              <ul className="space-y-3">
                {rows.map(({ icon: Icon, label, value }) => (
                  <li
                    key={label}
                    className="flex items-start gap-3"
                    data-testid={`property-location-modal-directions-${label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <span className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full border border-[#C16542]/40 bg-[#C16542]/[0.06] text-[#C16542]">
                      <Icon className="w-4 h-4" strokeWidth={1.8} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] tracking-[0.28em] uppercase text-[#5C5248]/80">
                        {label}
                      </p>
                      <p className="mt-0.5 text-sm text-[#2C2621] leading-snug">
                        {value}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Coordinates + Email row */}
          <section
            className="mt-6 border-t border-[#2C2621]/10 pt-5 grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="property-location-modal-meta"
          >
            {Array.isArray(coords) && coords.length === 2 ? (
              <div data-testid="property-location-modal-coords">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#A07042]">
                  {pick(COPY.coordinates, lang)}
                </p>
                <p className="mt-1.5 text-sm tabular-nums text-[#2C2621] font-mono select-all">
                  {formatCoord(coords[0])}, {formatCoord(coords[1])}
                </p>
              </div>
            ) : null}

            <div data-testid="property-location-modal-email">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#A07042]">
                {pick(COPY.contactEmail, lang)}
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(hotel.name)}`}
                className="mt-1.5 inline-flex items-center gap-2 text-sm text-[#C16542] hover:text-[#A35133] underline underline-offset-4 decoration-[#C16542]/40 hover:decoration-[#A35133] transition-colors"
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={1.8} />
                {CONTACT_EMAIL}
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PropertyLocationModal;
